import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react";
import { api } from "@/api/client";

export type Role = {
    code: string;
    name: string;
    permissions: string[];
};

export type Membership = {
    membership_id: string | null;
    platform_access?: boolean;
    tenant: {
        id: string;
        name: string;
        is_demo: boolean;
        onboarding_completed: boolean;
        onboarding_step: number;
        language: string;
        currency: string;
        timezone: string;
        country?: string | null;
        activity_types: string[];
    };
    role: Role | null;
    scope_branch_ids: string[];
    portal_person_id?: string | null;
};

export type Me = {
    user: {
        id: string;
        email: string;
        full_name: string | null;
        locale: string;
        is_platform_owner: boolean;
    };
    memberships: Membership[];
    active_tenant_id?: string | null;
};

export type PlanSummary = {
    code: string;
    name: string;
    modules: string[];
};

type AuthState = {
    status: "loading" | "authenticated" | "anonymous";
    me: Me | null;
    activeTenantId: string | null;
    refresh: () => Promise<void>;
    logout: () => Promise<void>;
    login: (email: string, password: string, tenant_id?: string) => Promise<void>;
    switchTenant: (tenant_id: string) => Promise<void>;
    exitTenant: () => Promise<void>;
    activeMembership: Membership | null;
    hasPerm: (perm: string) => boolean;
    plan: PlanSummary | null;
    hasModule: (module: string) => boolean;
    hasModuleAccess: (module: string, action?: "view" | "edit" | "delete") => boolean;
    effectivePermissions: string[] | null;
    effectiveModules: string[] | null;
    effectiveAccess: Record<string, Record<string, boolean>> | null;
    refreshEffectiveAccess: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [status, setStatus] = useState<AuthState["status"]>("loading");
    const [me, setMe] = useState<Me | null>(null);
    const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
    const [plan, setPlan] = useState<PlanSummary | null>(null);
    const [effectivePermissions, setEffectivePermissions] = useState<string[] | null>(null);
    const [effectiveModules, setEffectiveModules] = useState<string[] | null>(null);
    const [effectiveAccess, setEffectiveAccess] = useState<Record<string, Record<string, boolean>> | null>(null);

    const refreshEffectiveAccess = useCallback(async () => {
        try {
            const { data } = await api.get("/me/permissions");
            setEffectivePermissions(data?.effective_permissions ?? null);
            setEffectiveModules(data?.effective_modules ?? null);
            setEffectiveAccess(data?.effective_access ?? null);
        } catch {
            setEffectivePermissions(null);
            setEffectiveModules(null);
            setEffectiveAccess(null);
        }
    }, []);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get<Me>("/auth/me");
            setMe(data);
            const stored = localStorage.getItem("eduerp.active_tenant");
            const found = data.memberships.find((m) => m.tenant.id === stored);
            // Platform owner: the JWT's active tenant is the source of truth (F-13).
            const pick = data.user.is_platform_owner
                ? data.memberships.find((m) => m.tenant.id === data.active_tenant_id) ?? null
                : found ?? data.memberships[0] ?? null;
            setActiveTenantId(pick?.tenant.id ?? null);
            setStatus("authenticated");
        } catch {
            setMe(null);
            setActiveTenantId(null);
            setPlan(null);
            setStatus("anonymous");
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Fetch plan/module info whenever the active tenant changes.
    useEffect(() => {
        if (!activeTenantId || me?.user?.is_platform_owner) {
            setPlan(null);
            setEffectivePermissions(null);
            setEffectiveModules(null);
            setEffectiveAccess(null);
            return;
        }
        let mounted = true;
        api.get("/institution/subscription")
            .then((r) => {
                if (!mounted) return;
                const p = r.data?.subscription?.plan;
                if (p) setPlan({ code: p.code, name: p.name, modules: p.modules || [] });
                else setPlan(null);
            })
            .catch(() => { if (mounted) setPlan(null); });
        refreshEffectiveAccess();
        return () => { mounted = false; };
    }, [activeTenantId, me?.user?.is_platform_owner, refreshEffectiveAccess]);

    // Phase TAC-1 hotfix: platform owner can flip a module ON/OFF from a
    // separate tab; refetch the effective access map when the user returns
    // to this tab so the sidebar/menu updates without a full re-login.
    // (Skipped for platform owners — they always see everything.)
    useEffect(() => {
        if (!activeTenantId || me?.user?.is_platform_owner) return;
        const onFocus = () => { refreshEffectiveAccess(); };
        const onVisibility = () => {
            if (document.visibilityState === "visible") refreshEffectiveAccess();
        };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [activeTenantId, me?.user?.is_platform_owner, refreshEffectiveAccess]);

    const login = useCallback(
        async (email: string, password: string, tenant_id?: string) => {
            await api.post("/auth/login", { email, password, tenant_id });
            await refresh();
        },
        [refresh]
    );

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* ignore */
        }
        setMe(null);
        setActiveTenantId(null);
        setPlan(null);
        setStatus("anonymous");
        localStorage.removeItem("eduerp.active_tenant");
    }, []);

    const switchTenant = useCallback(
        async (tenant_id: string) => {
            await api.post("/auth/switch-tenant", { tenant_id });
            localStorage.setItem("eduerp.active_tenant", tenant_id);
            setActiveTenantId(tenant_id);
            await refresh();
        },
        [refresh]
    );

    const exitTenant = useCallback(async () => {
        await api.post("/auth/exit-tenant");
        localStorage.removeItem("eduerp.active_tenant");
        setActiveTenantId(null);
        await refresh();
    }, [refresh]);

    const activeMembership = useMemo(
        () => me?.memberships.find((m) => m.tenant.id === activeTenantId) ?? null,
        [me, activeTenantId]
    );

    const hasPerm = useCallback(
        (perm: string) => {
            if (!me) return false;
            if (me.user.is_platform_owner) return true;
            // Prefer server-computed effective set (role ∪ grants − denies) when known.
            if (effectivePermissions) return effectivePermissions.includes(perm);
            return !!activeMembership?.role?.permissions.includes(perm);
        },
        [me, activeMembership, effectivePermissions]
    );

    const hasModule = useCallback(
        (module: string) => {
            if (!me) return false;
            if (me.user.is_platform_owner) return true;
            // Prefer server-computed effective module set (plan ∩ activity ∩ platform overrides).
            if (effectiveModules) return effectiveModules.includes(module);
            // No plan info yet → don't render module-gated nav items (avoids flicker).
            if (!plan) return false;
            return plan.modules.includes(module);
        },
        [me, plan, effectiveModules]
    );

    /** Phase TAC-1: fine-grained module gate. Prefers the effective_access map
     * returned by /me/permissions when known; falls back to hasModule. */
    const hasModuleAccess = useCallback(
        (module: string, action: "view" | "edit" | "delete" = "view") => {
            if (!me) return false;
            if (me.user.is_platform_owner) return true;
            if (effectiveAccess && effectiveAccess[module]) {
                return !!effectiveAccess[module][action];
            }
            return hasModule(module);
        },
        [me, effectiveAccess, hasModule]
    );

    const value: AuthState = {
        status,
        me,
        activeTenantId,
        refresh,
        logout,
        login,
        switchTenant,
        exitTenant,
        activeMembership,
        hasPerm,
        plan,
        hasModule,
        hasModuleAccess,
        effectivePermissions,
        effectiveModules,
        effectiveAccess,
        refreshEffectiveAccess,
    };

    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error("useAuth outside AuthProvider");
    return ctx;
}
